import Layout from '@layout/index';
import Batch from '@cont/Batch/Batch';
import {
  fetchBatchData,
  fetchTopicsCovered,
  fetchTestSeries,
  fetchSchedule,
  fetchTrialClasses,
  fetchBatchFaq,
  fetchBatchFeedbacks,
  fetchBatchSkuData,
  fetchStorePlaylistFeed,
  fetchStorePlaylistTestFeed
} from '@api/fetchers/batch';
import { STORE_BATCH_PAGE } from '@constants/segment';
import { useSelector } from 'react-redux';
import { getMe } from '@selectors/auth';
// SWR and API functions
import { SWRConfig } from 'swr';
import { getKey as getBatchDetailsKey } from '@api/hooks/batch/useBatchDetails';
import useBatchSchedule from '@api/hooks/batch/useBatchSchedule';
import { getKey as getBatchTopickey } from '@api/hooks/batch/useBatchTopicsCovered';
import { getKey as getBatchTestSeriesKey } from '@api/hooks/batch/useBatchTestSeries';
import { getKey as getBatchTrailkey } from '@api/hooks/batch/useBatchTrialClasses';
import { getKey as getBatchFeedback } from '@api/hooks/batch/useBatchFeedback';
import { getKey as getBatchSkuDetailsKey } from '@api/hooks/batch/useBatchSkuDetails';
import { getKey as getStorePlaylistFeed } from '@api/hooks/batch/useStorePlaylistFeed';
import { getKey as getStorePlaylistTestFeed } from '@api/hooks/batch/useStorePlaylistTestFeed';

import escape from '@utils/escapeHTML';
import PageReduxProvider from '@cont/App/PageReduxProviderWrapper';
import OneLinkSmartScript from '@Analytics/OneLinkSmartScript';
import { FETCH_STORE_SYLLABUS_VALUES } from '@cont/Batch/utils';
import fetchPageMetaInfo from '@api/fetchers/pageMetaInfo';
import SEO from '@seo/index';
import batchSchema from '@seo/batch/batch';
import useGoalInfo from '@api/hooks/goal/useGoalInfo';
import { emptyObject } from '@constants/empty';
import { wordsToSentence } from '@utils/textHelper';

const BatchPage = ({ batchUID, fallbackData }) => {
  const me = useSelector(getMe);
  const { faqs = [] } = fallbackData || {};

  return <Batch batchUID={batchUID} me={me} faqs={faqs} hasOwnSEO />;
};

const BatchPageWithSWR = ({
  goalUID,
  batchUID,
  fallbackData,
  pageMetaInfo
}) => {
  const batchDetailsKey = getBatchDetailsKey(batchUID);
  const { key: batchScheduleKey } = useBatchSchedule(batchUID);
  const batchTopicsCoveredKey = getBatchTopickey(batchUID);
  const batchTestSeriesKey = getBatchTestSeriesKey(batchUID);
  const batchTrialClassesKey = getBatchTrailkey(batchUID);
  const batchfeedbackKey = getBatchFeedback(goalUID);
  const batchSkuDetailsKey = getBatchSkuDetailsKey(goalUID, batchUID);
  const storePlaylistFeed = getStorePlaylistFeed(batchUID);
  const storePlaylistTestFeed = getStorePlaylistTestFeed({ batchUID });

  const { title: cmsTitle, description: cmsDescription } = pageMetaInfo;
  const batchDetails = fallbackData?.batchDetails;
  const batchSlug = batchDetails.slug;
  const canonicalURL = batchSlug
    ? `https://unacademy.com/store/batch/${batchSlug}/${batchUID}`
    : null;
  const goalName = batchDetails.goal?.name || 'Goal';
  const batchName = batchDetails.name || 'Batch';
  const metaTitle =
    cmsTitle || `${goalName} - ${batchName} Offered by Unacademy`;
  const metaDescription =
    cmsDescription ||
    `${goalName} - ${batchName} is a course in ${wordsToSentence(
      batchDetails.languages,
      'label',
      ', ',
      ', '
    )} delivered by Top Educators. Join Unacademy to access the course.`;
  const { goalInfo: { ogImage } = emptyObject } =
    useGoalInfo(goalUID) ?? emptyObject;
  const imageURL =
    ogImage ||
    'https://static.uacdn.net/static/dist/c76c2e18c7980818a913f9467c74c567.png';

  return (
    <SWRConfig
      value={{
        revalidateOnMount: false,
        dedupingInterval: 60 * 1000,
        fallback: {
          [batchDetailsKey]: fallbackData?.batchDetails,
          [batchScheduleKey]: fallbackData?.schedule,
          [batchTopicsCoveredKey]: fallbackData?.topicsCovered,
          [batchTestSeriesKey]: fallbackData?.testSeries,
          [batchTrialClassesKey]: fallbackData?.trialClasses,
          [batchfeedbackKey]: fallbackData?.feedback,
          [batchSkuDetailsKey]: fallbackData?.skuDetails,
          [storePlaylistFeed]: fallbackData?.storePlaylistFeed,
          [storePlaylistTestFeed]: fallbackData?.storePlaylistTestFeed
        }
      }}
    >
      <SEO
        title={metaTitle}
        description={metaDescription}
        url={canonicalURL}
        openGraph={{
          title: metaTitle,
          description: metaDescription,
          images: [
            {
              url: imageURL
            }
          ],
          url: canonicalURL
        }}
        schema={
          canonicalURL
            ? batchSchema({
                batch: { name: batchDetails.name, permalink: canonicalURL },
                batchDetails
              })
            : null
        }
      />
      <BatchPage batchUID={batchUID} fallbackData={fallbackData} />
      <OneLinkSmartScript />
    </SWRConfig>
  );
};

BatchPageWithSWR.pageInfo = {
  name: STORE_BATCH_PAGE,
  isCMSMeta: true,
  isMWebSupported: true
};

export default BatchPageWithSWR;

export const getStaticProps = async ({ params }) => {
  try {
    const { batchUID } = params;
    const promises = [];
    const batchDetails = await fetchBatchData(batchUID);
    const { goal } = batchDetails;

    promises.push(fetchTopicsCovered(batchUID));
    promises.push(fetchTestSeries(batchUID));
    promises.push(fetchSchedule(batchUID));
    promises.push(fetchTrialClasses(batchUID));
    promises.push(fetchBatchFaq('storebatchesfaqs'));
    promises.push(fetchStorePlaylistFeed(batchUID));
    promises.push(
      fetchStorePlaylistTestFeed({
        batchUID,
        offset: FETCH_STORE_SYLLABUS_VALUES.OFFSET
      })
    );
    promises.push(fetchBatchFeedbacks(goal.uid));
    promises.push(fetchBatchSkuData(batchUID, goal.uid));
    promises.push(fetchPageMetaInfo(BatchPageWithSWR.pageInfo.name, batchUID));

    const [
      topicsCovered,
      testSeries,
      schedule,
      trialClasses,
      faqsResponse,
      storePlaylistFeed,
      storePlaylistTestFeed,
      feedback,
      batchSkuDetails,
      metaInfo
    ] = await Promise.all(promises);

    // If batch is not public, but exists, let it render and revalidate it on the client-side.
    if (batchDetails?.errorCode === 'E11002')
      return {
        props: { batchUID, faqs: faqsResponse?.faqs },
        revalidate: 5 * 60
      };
    return {
      props: {
        goalUID: goal.uid,
        batchUID: escape(batchUID),
        pageMetaInfo: metaInfo,
        fallbackData: {
          batchDetails,
          topicsCovered,
          testSeries,
          schedule,
          trialClasses,
          feedback: feedback || { results: [] },
          faqs: faqsResponse?.faqs,
          batchSkuDetails,
          storePlaylistFeed,
          storePlaylistTestFeed
        }
      },
      revalidate: 3 * 60
    };
  } catch {
    return { notFound: true, revalidate: 60 };
  }
};

export const getStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking'
  };
};

BatchPageWithSWR.Layout = Layout;
BatchPageWithSWR.Wrapper = PageReduxProvider;
